import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import { G300, R300 } from '@atlaskit/theme/colors';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import useResendEmailAPI, {
  ResendEmailStatuses
} from '../../../../../../hook/useResendEmailAPI';
import useResendEmailData from '../../../../../../hook/useResendEmailData';
import { CheckEmailAnalyticsInterface } from '../../../../../../model/check-email-page';
import { CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES } from '../../../../../../model/check-email-page';
import { messages } from '../../messages';
import { CharlieDisplayFontStack, RotationKeyframes } from '../../styled';

const RESEND_ATTEMPT_LIMIT = 3;

const StyledButton = styled(Button)`
  font-family: ${CharlieDisplayFontStack} !important;
  line-height: 15px !important;
`;

const Container = styled.div`
  position: relative;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: -36px;
  top: 50%;
  transform: translateY(-50%);
  height: 32px;
  width: 32px;
`;

const Rotation = styled.div`
  animation: ${RotationKeyframes} 1.25s linear 0s infinite;
`;

interface ResendEmailButtonProps {
  baseUrl?: string;
  checkEmailAnalyticsService: CheckEmailAnalyticsInterface;
}

const ResendEmailButton: React.FC<ResendEmailButtonProps> = ({
  baseUrl,
  checkEmailAnalyticsService
}) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [resendEmailData, incrementResendCount] = useResendEmailData();
  const { state: resendEmailState, triggerResendEmail } = useResendEmailAPI({
    baseUrl,
    checkEmailAnalyticsService
  });

  if (
    resendEmailData.resendCount > RESEND_ATTEMPT_LIMIT ||
    resendEmailData.resendCount < 0
  ) {
    return (
      <>
        <FormattedMessage {...messages.resendEmailStillNoEmail} />
        <a href="/company/contact">
          <FormattedMessage {...messages.resendEmailCreateAHelpTicket} />
        </a>
      </>
    );
  }

  const onClick = () => {
    const newCount = resendEmailData.resendCount + 1;
    if (newCount > RESEND_ATTEMPT_LIMIT) {
      return incrementResendCount();
    }

    checkEmailAnalyticsService.clickResendButton(createAnalyticsEvent);

    triggerResendEmail(resendEmailData).then((success) => {
      if (success) {
        incrementResendCount();
        createAnalyticsEvent({
          action: 'resendEmail',
          name: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_API_SUCCESS
        }).fire();
      } else {
        createAnalyticsEvent({
          action: 'resendEmail',
          name: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_API_FAILURE
        }).fire();
      }
    });
  };

  const Icon = () => {
    switch (resendEmailState.status) {
      case ResendEmailStatuses.SUCCESS:
        return (
          <CheckCircleIcon
            label="resend email success"
            primaryColor={G300}
            size="large"
          />
        );
      case ResendEmailStatuses.FAILURE:
        return (
          <CrossCircleIcon
            label="resend email failure"
            primaryColor={R300}
            size="large"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <StyledButton
        appearance="primary"
        onClick={onClick}
        isDisabled={resendEmailState.status === ResendEmailStatuses.REQUESTING}
        iconAfter={
          resendEmailState.status === ResendEmailStatuses.REQUESTING ? (
            <Rotation>
              <RefreshIcon label="resend email requesting" />
            </Rotation>
          ) : undefined
        }
      >
        <FormattedMessage {...messages.resendEmailAction} />
      </StyledButton>
      <IconContainer>
        <Icon />
      </IconContainer>
    </Container>
  );
};

export default injectIntl(ResendEmailButton);
